/**
 * Full original runOrchestrationLoop semantics from golden-master App.tsx.
 * No experimental gates between Archivist and commit.
 */
import type {
  Chapter,
  ChapterPlan,
  EditorResult,
  Ledger,
  PublicationEditorResult,
  SoftStateBrief,
  StoryBible,
  ValidationResult,
} from '../../types';
import {
  editChapter,
  generateChapter,
  generateDirectorsNote,
  publishChapter,
  replanCurrentChapter,
  rewriteDeadlockedDraft,
  updateLedger,
  validateChapter,
} from './gemini.agents';
import {
  MAX_EDITOR_RETRIES,
  ULTIMATE_DEADLOCK_RETRY,
  type OriginalChapterOrchestrationResult,
} from './goldenOrchestration';

export interface OriginalOrchestrationLoopState {
  storyBible: StoryBible;
  ledger: Ledger;
  chapterNumber: number;
  previousChapters: Chapter[];
  retryCount: number;
  correctionOrder?: string;
  failedDraft?: string;
  directorsNote?: string;
  editorRetries: number;
  editorFeedback?: string;
  softState?: SoftStateBrief | null;
  inspectorCallCount: number;
  scribeCorrectionCount: number;
  escalationEvents: string[];
}

export type OriginalOrchestrationIterationResult =
  | {
      kind: 'INSPECTOR_BLOCKED';
      state: OriginalOrchestrationLoopState;
      validation: ValidationResult;
      correctionOrder: string;
    }
  | { kind: 'ULTIMATE_DEADLOCK'; state: OriginalOrchestrationLoopState }
  | { kind: 'SUCCESS'; state: OriginalOrchestrationLoopState; result: OriginalChapterOrchestrationResult };

function fallbackPlan(chapterNumber: number): ChapterPlan {
  return {
    chapterNumber,
    title: `Chapter ${chapterNumber}`,
    summary: 'Advance the plot.',
    requiredBeats: [],
    status: 'planned',
    id: 'fallback',
    updatedAt: new Date().toISOString(),
  };
}

function planForChapter(bible: StoryBible, chapterNumber: number): ChapterPlan {
  return bible.chapterPlans?.find((p) => p?.chapterNumber === chapterNumber) || fallbackPlan(chapterNumber);
}

export function createInitialLoopState(input: {
  storyBible: StoryBible;
  ledger: Ledger;
  chapterNumber: number;
  previousChapters: Chapter[];
  directorsNote?: string;
  correctionOrder?: string;
  failedDraft?: string;
  retryCount?: number;
}): OriginalOrchestrationLoopState {
  return {
    storyBible: input.storyBible,
    ledger: input.ledger,
    chapterNumber: input.chapterNumber,
    previousChapters: input.previousChapters,
    retryCount: input.retryCount ?? (input.correctionOrder ? 1 : 0),
    correctionOrder: input.correctionOrder,
    failedDraft: input.failedDraft,
    directorsNote: input.directorsNote,
    editorRetries: 0,
    editorFeedback: undefined,
    softState: null,
    inspectorCallCount: 0,
    scribeCorrectionCount: input.correctionOrder ? 1 : 0,
    escalationEvents: [],
  };
}

export type OriginalOrchestrationStage =
  | 'SCRIBE_COMPLETE'
  | 'INSPECTOR_COMPLETE'
  | 'EDITOR_COMPLETE'
  | 'PUBLICATION_EDITOR_COMPLETE'
  | 'ARCHIVIST_COMPLETE';

export interface OriginalOrchestrationStageHookPayload {
  stage: OriginalOrchestrationStage;
  state: OriginalOrchestrationLoopState;
  chapterDraft?: Chapter;
  inspectorResult?: ValidationResult;
  editorResult?: EditorResult;
  publicationResult?: PublicationEditorResult;
  updatedLedger?: Ledger;
}

export interface OriginalOrchestrationStageHooks {
  onStageComplete?: (payload: OriginalOrchestrationStageHookPayload) => Promise<void>;
}

export async function runOriginalOrchestrationIteration(
  state: OriginalOrchestrationLoopState,
  hooks?: OriginalOrchestrationStageHooks,
): Promise<OriginalOrchestrationIterationResult> {
  const bible = state.storyBible;
  const chapterNumber = state.chapterNumber;
  const plan = planForChapter(bible, chapterNumber);
  const prevSummary =
    state.previousChapters.length > 0
      ? state.previousChapters[state.previousChapters.length - 1].summary
      : 'The story begins.';

  if (state.retryCount >= ULTIMATE_DEADLOCK_RETRY) {
    return { kind: 'ULTIMATE_DEADLOCK', state };
  }

  let editorRetries = state.editorRetries;
  let editorFeedback = state.editorFeedback;
  let finalDraft: Chapter | null = null;
  let lastInspector: ValidationResult | null = null;
  let lastEditor: EditorResult | null = null;

  while (editorRetries <= MAX_EDITOR_RETRIES) {
    let currentDraft: Chapter;

    if (state.retryCount === 6) {
      state.escalationEvents.push('DIRECTOR_REWRITE');
      currentDraft = await rewriteDeadlockedDraft(
        state.failedDraft || '',
        state.correctionOrder || '',
        state.ledger,
        plan,
        prevSummary,
        bible,
        state.softState,
      );
    } else {
      currentDraft = await generateChapter(
        bible,
        state.ledger,
        chapterNumber,
        prevSummary,
        state.correctionOrder,
        editorFeedback,
        state.directorsNote,
        state.retryCount,
        state.failedDraft,
        state.softState,
      );
    }

    await hooks?.onStageComplete?.({
      stage: 'SCRIBE_COMPLETE',
      state,
      chapterDraft: currentDraft,
    });

    const validation = await validateChapter(
      currentDraft,
      state.ledger,
      bible,
      state.directorsNote,
      state.retryCount,
      plan,
    );
    state.inspectorCallCount += 1;
    lastInspector = validation;

    if (validation.status === 'FAIL') {
      const fullFeedback = `REASON FOR REJECTION:\n${validation.reason || 'Continuity error detected'}\n\nREQUIRED CORRECTIONS:\n${validation.correction_order || 'Please ensure the narrative advances logically.'}`;
      const next: OriginalOrchestrationLoopState = { ...state, storyBible: { ...bible } };

      if (state.retryCount === 3) {
        next.escalationEvents = [...state.escalationEvents, 'ARCHITECT_REPLAN'];
        try {
          const revisedPlan = await replanCurrentChapter(
            plan,
            state.ledger,
            validation.reason || 'Continuity error',
            prevSummary,
          );
          const plans = [...(bible.chapterPlans || [])];
          const idx = plans.findIndex((p) => p?.chapterNumber === chapterNumber);
          if (idx !== -1) plans[idx] = revisedPlan;
          next.storyBible = { ...bible, chapterPlans: plans };
          next.failedDraft = undefined;
          next.correctionOrder = undefined;
        } catch {
          next.correctionOrder = fullFeedback;
        }
      } else if (state.retryCount === 5) {
        next.escalationEvents = [...state.escalationEvents, 'DIRECTOR_WARNING'];
        next.correctionOrder = fullFeedback;
      } else if (state.retryCount === 6) {
        next.escalationEvents = [...state.escalationEvents, 'DIRECTORS_NOTE'];
        try {
          next.directorsNote = await generateDirectorsNote(
            currentDraft.content,
            validation.reason || 'Continuity error',
            state.ledger,
            plan,
          );
          next.failedDraft = undefined;
          next.correctionOrder = undefined;
        } catch {
          next.correctionOrder = fullFeedback;
        }
      } else {
        next.correctionOrder = fullFeedback;
      }

      if (state.retryCount !== 3 && state.retryCount !== 6) {
        next.failedDraft = currentDraft.content;
      }
      next.retryCount = state.retryCount + 1;
      next.scribeCorrectionCount = state.scribeCorrectionCount + 1;
      next.editorRetries = 0;
      next.editorFeedback = undefined;

      return {
        kind: 'INSPECTOR_BLOCKED',
        state: next,
        validation,
        correctionOrder: next.correctionOrder || fullFeedback,
      };
    }

    await hooks?.onStageComplete?.({
      stage: 'INSPECTOR_COMPLETE',
      state,
      chapterDraft: currentDraft,
      inspectorResult: validation,
    });

    const editorResult = await editChapter(currentDraft, bible, state.ledger, plan);
    lastEditor = editorResult;

    if (editorResult.status === 'APPROVED' || editorRetries === MAX_EDITOR_RETRIES) {
      finalDraft = {
        ...currentDraft,
        content: editorResult.polished_text || currentDraft.content,
      };
      break;
    }

    editorFeedback = editorResult.feedback;
    editorRetries += 1;
  }

  if (!finalDraft || !lastInspector || !lastEditor) {
    throw new Error('Original orchestration iteration failed to produce final draft.');
  }

  await hooks?.onStageComplete?.({
    stage: 'EDITOR_COMPLETE',
    state,
    chapterDraft: finalDraft,
    inspectorResult: lastInspector,
    editorResult: lastEditor,
  });

  const publishResult = await publishChapter(finalDraft, bible, state.ledger, plan);
  const publishedChapter: Chapter = {
    ...finalDraft,
    content: publishResult.patched_content,
  };

  await hooks?.onStageComplete?.({
    stage: 'PUBLICATION_EDITOR_COMPLETE',
    state,
    chapterDraft: publishedChapter,
    inspectorResult: lastInspector,
    editorResult: lastEditor,
    publicationResult: publishResult,
  });

  const updatedLedger = await updateLedger(publishedChapter, state.ledger, bible.mode);

  await hooks?.onStageComplete?.({
    stage: 'ARCHIVIST_COMPLETE',
    state,
    chapterDraft: publishedChapter,
    inspectorResult: lastInspector,
    editorResult: lastEditor,
    publicationResult: publishResult,
    updatedLedger,
  });

  const result: OriginalChapterOrchestrationResult = {
    chapter: publishedChapter,
    ledger: updatedLedger,
    inspectorResult: lastInspector,
    editorResult: lastEditor,
    publicationResult: publishResult,
    correctionCount: state.scribeCorrectionCount,
    editorRetryCount: state.editorRetries,
    nextChapterNumber: chapterNumber + 1,
  };

  const successState: OriginalOrchestrationLoopState = {
    ...state,
    ledger: updatedLedger,
    retryCount: 0,
    correctionOrder: undefined,
    failedDraft: undefined,
    editorRetries: 0,
    editorFeedback: undefined,
  };

  return { kind: 'SUCCESS', state: successState, result };
}

export async function runOriginalOrchestrationToCompletion(
  initial: OriginalOrchestrationLoopState,
  maxIterations = 12,
  hooks?: OriginalOrchestrationStageHooks,
): Promise<OriginalChapterOrchestrationResult & { loopState: OriginalOrchestrationLoopState }> {
  let state = initial;
  for (let i = 0; i < maxIterations; i += 1) {
    const step = await runOriginalOrchestrationIteration(state, hooks);
    if (step.kind === 'SUCCESS') {
      return { ...step.result, loopState: step.state };
    }
    if (step.kind === 'ULTIMATE_DEADLOCK') {
      throw new Error('ULTIMATE_DEADLOCK');
    }
    state = step.state;
  }
  throw new Error('Original orchestration exceeded iteration budget');
}

async function continueFromInspectorPass(
  state: OriginalOrchestrationLoopState,
  currentDraft: Chapter,
  validation: ValidationResult,
  hooks?: OriginalOrchestrationStageHooks,
): Promise<OriginalChapterOrchestrationResult & { loopState: OriginalOrchestrationLoopState }> {
  const bible = state.storyBible;
  const chapterNumber = state.chapterNumber;
  const plan = planForChapter(bible, chapterNumber);

  await hooks?.onStageComplete?.({
    stage: 'INSPECTOR_COMPLETE',
    state,
    chapterDraft: currentDraft,
    inspectorResult: validation,
  });

  let editorRetries = state.editorRetries;
  let editorFeedback = state.editorFeedback;
  let finalDraft: Chapter | null = null;
  let lastEditor: EditorResult | null = null;

  while (editorRetries <= MAX_EDITOR_RETRIES) {
    const editorResult = await editChapter(currentDraft, bible, state.ledger, plan);
    lastEditor = editorResult;

    if (editorResult.status === 'APPROVED' || editorRetries === MAX_EDITOR_RETRIES) {
      finalDraft = {
        ...currentDraft,
        content: editorResult.polished_text || currentDraft.content,
      };
      break;
    }

    editorFeedback = editorResult.feedback;
    editorRetries += 1;
  }

  if (!finalDraft || !lastEditor) {
    throw new Error('Original orchestration resume failed at editor stage.');
  }

  await hooks?.onStageComplete?.({
    stage: 'EDITOR_COMPLETE',
    state,
    chapterDraft: finalDraft,
    inspectorResult: validation,
    editorResult: lastEditor,
  });

  const publishResult = await publishChapter(finalDraft, bible, state.ledger, plan);
  const publishedChapter: Chapter = {
    ...finalDraft,
    content: publishResult.patched_content,
  };

  await hooks?.onStageComplete?.({
    stage: 'PUBLICATION_EDITOR_COMPLETE',
    state,
    chapterDraft: publishedChapter,
    inspectorResult: validation,
    editorResult: lastEditor,
    publicationResult: publishResult,
  });

  const updatedLedger = await updateLedger(publishedChapter, state.ledger, bible.mode);

  await hooks?.onStageComplete?.({
    stage: 'ARCHIVIST_COMPLETE',
    state,
    chapterDraft: publishedChapter,
    inspectorResult: validation,
    editorResult: lastEditor,
    publicationResult: publishResult,
    updatedLedger,
  });

  const result: OriginalChapterOrchestrationResult = {
    chapter: publishedChapter,
    ledger: updatedLedger,
    inspectorResult: validation,
    editorResult: lastEditor,
    publicationResult: publishResult,
    correctionCount: state.scribeCorrectionCount,
    editorRetryCount: editorRetries,
    nextChapterNumber: chapterNumber + 1,
  };

  return {
    ...result,
    loopState: {
      ...state,
      ledger: updatedLedger,
      retryCount: 0,
      correctionOrder: undefined,
      failedDraft: undefined,
      editorRetries: 0,
      editorFeedback: undefined,
    },
  };
}

function buildResultFromArchivistCheckpoint(input: {
  state: OriginalOrchestrationLoopState;
  chapter: Chapter;
  ledger: Ledger;
  inspectorResult: ValidationResult;
  editorResult: EditorResult;
  publicationResult: PublicationEditorResult;
}): OriginalChapterOrchestrationResult & { loopState: OriginalOrchestrationLoopState } {
  const result: OriginalChapterOrchestrationResult = {
    chapter: input.chapter,
    ledger: input.ledger,
    inspectorResult: input.inspectorResult,
    editorResult: input.editorResult,
    publicationResult: input.publicationResult,
    correctionCount: input.state.scribeCorrectionCount,
    editorRetryCount: input.state.editorRetries,
    nextChapterNumber: input.state.chapterNumber + 1,
  };
  return {
    ...result,
    loopState: {
      ...input.state,
      ledger: input.ledger,
      retryCount: 0,
      correctionOrder: undefined,
      failedDraft: undefined,
      editorRetries: 0,
      editorFeedback: undefined,
    },
  };
}

/**
 * Infrastructure-only resume entry: skips stages already completed in a compat checkpoint.
 */
export async function runOriginalOrchestrationFromResumeStage(
  state: OriginalOrchestrationLoopState,
  completedStage: OriginalOrchestrationStage,
  artifacts: {
    chapterDraft: Chapter;
    inspectorResult?: ValidationResult;
    editorResult?: EditorResult;
    publicationResult?: PublicationEditorResult;
    updatedLedger?: Ledger;
  },
  hooks?: OriginalOrchestrationStageHooks,
): Promise<OriginalChapterOrchestrationResult & { loopState: OriginalOrchestrationLoopState }> {
  const bible = state.storyBible;
  const chapterNumber = state.chapterNumber;
  const plan = planForChapter(bible, chapterNumber);

  if (completedStage === 'ARCHIVIST_COMPLETE') {
    if (!artifacts.inspectorResult || !artifacts.editorResult || !artifacts.publicationResult || !artifacts.updatedLedger) {
      throw new Error('ARCHIVIST_COMPLETE checkpoint missing required artifacts.');
    }
    return buildResultFromArchivistCheckpoint({
      state,
      chapter: artifacts.chapterDraft,
      ledger: artifacts.updatedLedger,
      inspectorResult: artifacts.inspectorResult,
      editorResult: artifacts.editorResult,
      publicationResult: artifacts.publicationResult,
    });
  }

  if (completedStage === 'PUBLICATION_EDITOR_COMPLETE') {
    if (!artifacts.inspectorResult || !artifacts.editorResult || !artifacts.publicationResult) {
      throw new Error('PUBLICATION_EDITOR_COMPLETE checkpoint missing required artifacts.');
    }
    const updatedLedger = await updateLedger(artifacts.chapterDraft, state.ledger, bible.mode);
    await hooks?.onStageComplete?.({
      stage: 'ARCHIVIST_COMPLETE',
      state,
      chapterDraft: artifacts.chapterDraft,
      inspectorResult: artifacts.inspectorResult,
      editorResult: artifacts.editorResult,
      publicationResult: artifacts.publicationResult,
      updatedLedger,
    });
    return buildResultFromArchivistCheckpoint({
      state,
      chapter: artifacts.chapterDraft,
      ledger: updatedLedger,
      inspectorResult: artifacts.inspectorResult,
      editorResult: artifacts.editorResult,
      publicationResult: artifacts.publicationResult,
    });
  }

  if (completedStage === 'EDITOR_COMPLETE') {
    if (!artifacts.inspectorResult || !artifacts.editorResult) {
      throw new Error('EDITOR_COMPLETE checkpoint missing required artifacts.');
    }
    const publishResult = await publishChapter(artifacts.chapterDraft, bible, state.ledger, plan);
    const publishedChapter: Chapter = {
      ...artifacts.chapterDraft,
      content: publishResult.patched_content,
    };
    await hooks?.onStageComplete?.({
      stage: 'PUBLICATION_EDITOR_COMPLETE',
      state,
      chapterDraft: publishedChapter,
      inspectorResult: artifacts.inspectorResult,
      editorResult: artifacts.editorResult,
      publicationResult: publishResult,
    });
    const updatedLedger = await updateLedger(publishedChapter, state.ledger, bible.mode);
    await hooks?.onStageComplete?.({
      stage: 'ARCHIVIST_COMPLETE',
      state,
      chapterDraft: publishedChapter,
      inspectorResult: artifacts.inspectorResult,
      editorResult: artifacts.editorResult,
      publicationResult: publishResult,
      updatedLedger,
    });
    return buildResultFromArchivistCheckpoint({
      state,
      chapter: publishedChapter,
      ledger: updatedLedger,
      inspectorResult: artifacts.inspectorResult,
      editorResult: artifacts.editorResult,
      publicationResult: publishResult,
    });
  }

  if (completedStage === 'INSPECTOR_COMPLETE') {
    if (!artifacts.inspectorResult) {
      throw new Error('INSPECTOR_COMPLETE checkpoint missing inspector result.');
    }
    return continueFromInspectorPass(state, artifacts.chapterDraft, artifacts.inspectorResult, hooks);
  }

  if (completedStage === 'SCRIBE_COMPLETE') {
    const validation = await validateChapter(
      artifacts.chapterDraft,
      state.ledger,
      bible,
      state.directorsNote,
      state.retryCount,
      plan,
    );
    if (validation.status === 'FAIL') {
      const fullFeedback = `REASON FOR REJECTION:\n${validation.reason || 'Continuity error detected'}\n\nREQUIRED CORRECTIONS:\n${validation.correction_order || 'Please ensure the narrative advances logically.'}`;
      const next: OriginalOrchestrationLoopState = { ...state, storyBible: { ...bible } };
      if (state.retryCount === 3) {
        next.escalationEvents = [...state.escalationEvents, 'ARCHITECT_REPLAN'];
        try {
          const revisedPlan = await replanCurrentChapter(
            plan,
            state.ledger,
            validation.reason || 'Continuity error',
            state.previousChapters.length > 0
              ? state.previousChapters[state.previousChapters.length - 1].summary
              : 'The story begins.',
          );
          const plans = [...(bible.chapterPlans || [])];
          const idx = plans.findIndex((p) => p?.chapterNumber === chapterNumber);
          if (idx !== -1) plans[idx] = revisedPlan;
          next.storyBible = { ...bible, chapterPlans: plans };
          next.failedDraft = undefined;
          next.correctionOrder = undefined;
        } catch {
          next.correctionOrder = fullFeedback;
        }
      } else if (state.retryCount === 5) {
        next.escalationEvents = [...state.escalationEvents, 'DIRECTOR_WARNING'];
        next.correctionOrder = fullFeedback;
      } else if (state.retryCount === 6) {
        next.escalationEvents = [...state.escalationEvents, 'DIRECTORS_NOTE'];
        try {
          next.directorsNote = await generateDirectorsNote(
            artifacts.chapterDraft.content,
            validation.reason || 'Continuity error',
            state.ledger,
            plan,
          );
          next.failedDraft = undefined;
          next.correctionOrder = undefined;
        } catch {
          next.correctionOrder = fullFeedback;
        }
      } else {
        next.correctionOrder = fullFeedback;
      }
      if (state.retryCount !== 3 && state.retryCount !== 6) {
        next.failedDraft = artifacts.chapterDraft.content;
      }
      next.retryCount = state.retryCount + 1;
      next.scribeCorrectionCount = state.scribeCorrectionCount + 1;
      next.editorRetries = 0;
      next.editorFeedback = undefined;
      return runOriginalOrchestrationToCompletion(next, 12, hooks);
    }
    return continueFromInspectorPass(state, artifacts.chapterDraft, validation, hooks);
  }

  throw new Error(`Unsupported resume stage: ${completedStage}`);
}
